import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LookupDto,
  LookupInput,
  LookupKind,
  LookupOption,
  CoverageMode,
  Paged,
  ServiceAreaCoverageResult,
  ServiceAreaCoverageRow,
  ServiceAreaDto,
  ServiceAreaRemoveInput,
  ServiceAreaSummaryRow,
  ServiceAreaUpsertInput,
  SettingChangeDto,
  SettingDto,
  SettingValue,
} from '@app/shared';
import { api } from '../../lib/api';
import type { Query } from '../../lib/api';

/* ------------------------------ System Config ------------------------------ */

export const useSettings = () =>
  useQuery({
    queryKey: ['system-configs', 'list'],
    queryFn: () => api.get<{ data: SettingDto[] }>('/system-configs'),
    select: (r) => r.data,
  });

export const useSettingChanges = (params: Query, enabled: boolean) =>
  useQuery({
    queryKey: ['system-configs', 'changes', params],
    queryFn: () => api.get<Paged<SettingChangeDto>>('/system-configs/changes', params),
    placeholderData: (prev) => prev,
    enabled,
  });

export function useSaveSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, SettingValue>) =>
      api.patch<{ data: SettingDto[] }>('/system-configs', { values }),
    // Settings change what other screens offer (payment methods, required fields), so drop everything cached.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

/* ------------------------------- Service Area ------------------------------ */

export const useAreaSummary = () =>
  useQuery({
    queryKey: ['service-areas', 'summary'],
    queryFn: () => api.get<{ data: ServiceAreaSummaryRow[] }>('/service-areas/summary'),
    select: (r) => r.data,
  });

export const useAreaZips = (params: Query, enabled: boolean) =>
  useQuery({
    queryKey: ['service-areas', 'zips', params],
    queryFn: () => api.get<Paged<ServiceAreaDto>>('/service-areas', params),
    placeholderData: (prev) => prev,
    enabled,
  });

function useAreaMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['service-areas'] }),
  });
}

export const useUpsertArea = () =>
  useAreaMutation((input: ServiceAreaUpsertInput) =>
    api.post<{ added: number; updated: number }>('/service-areas', input),
  );
export const useRemoveArea = () =>
  useAreaMutation((input: ServiceAreaRemoveInput) =>
    api.post<{ changed: number; removed: number }>('/service-areas/remove', input),
  );
/** ZIP carrier routing: each carrier and how many ZIPs its coverage list holds. */
export const useCoverage = (enabled: boolean) =>
  useQuery({
    queryKey: ['service-areas', 'coverage'],
    queryFn: () => api.get<{ data: ServiceAreaCoverageRow[] }>('/service-areas/coverage'),
    select: (r) => r.data,
    enabled,
  });

export const useImportCoverage = () =>
  useAreaMutation((v: { carrierId: string; mode: CoverageMode; file: File }) => {
    const form = new FormData();
    form.append('carrierId', v.carrierId);
    form.append('mode', v.mode);
    form.append('file', v.file);
    return api.upload<ServiceAreaCoverageResult>('/service-areas/coverage', form);
  });

export const useBulkArea = () =>
  useAreaMutation((file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<{ added: number; updated: number }>('/service-areas/bulk', form);
  });

/* ------------------------- Other Settings (lists) -------------------------- */

export const useLookups = (kind: LookupKind, params: Query) =>
  useQuery({
    queryKey: ['lookups', kind, 'list', params],
    queryFn: () => api.get<Paged<LookupDto>>(`/lookups/${kind}`, params),
    placeholderData: (prev) => prev,
  });

/** Active entries of a managed list, for selects on other screens. */
export const useLookupOptions = (kind: LookupKind, enabled = true) =>
  useQuery({
    queryKey: ['lookups', kind, 'options'],
    queryFn: () => api.get<{ data: LookupOption[] }>(`/lookups/${kind}/options`),
    select: (r) => r.data,
    enabled,
  });

function useLookupMutation<TVars, TResult>(
  kind: LookupKind,
  fn: (vars: TVars) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lookups', kind] }),
  });
}

export const useCreateLookup = (kind: LookupKind) =>
  useLookupMutation(kind, (input: LookupInput) => api.post<LookupDto>(`/lookups/${kind}`, input));
export const useUpdateLookup = (kind: LookupKind) =>
  useLookupMutation(kind, ({ id, input }: { id: string; input: Partial<LookupInput> }) =>
    api.patch<LookupDto>(`/lookups/${kind}/${id}`, input),
  );
